// =====================
// Imports
// =====================
import AffiliateInfo from '@/components/affiliate/AffiliateInfo';
import OrderStatusBadge from '@/components/orders/order-status-badge';
import { OrderRewardBadge } from '@/components/orders/order-reward-badge';
import { CommissionRewardReviewNotice } from '@/components/orders/commission-reward-review-notice';
import SubscriptionSaleBadge from '@/components/orders/subscription-sale-badge';
import EcommercePlatformBadge from '@/components/shared/ecommerce-platform-badge';
import PaginationControls from '@/components/shared/PaginationControls';
import TableEmptyState from '@/components/table/TableEmptyState';
import TableSkeleton from '@/components/table/TableSkeleton';
import { TableScrollArea, stickyColumnProps } from '@/components/table/TableScrollArea';
import DateCell from '@/components/table/DateCell';
import Money from '@/components/text/money';
import type { Pagination } from '@/types/pagination';
import type { EcommercePlatforms } from '@/types/platform';
import { Box, Button, HStack, Table, VStack } from '@chakra-ui/react';
import {
  formatExactCommissionMoney,
  getCommissionSourcePresentation,
  type Commission,
  type PaginatedResponse,
} from '@fluenceur/types';
import { MoneySquare } from 'iconoir-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RecurringCommissionDetailsDrawer } from './RecurringCommissionDetailsDrawer';

// =====================
// Interfaces
// =====================
interface CommissionHistoryTableProps {
  commissions: Commission[];
  pagination?: PaginatedResponse<Commission>['meta'] | null;
  loading?: boolean;
  onPageChange?: (page: number) => void;
}

// =====================
// Main Component
// =====================
// Helper function to adapt pagination format
const adaptPagination = (meta: PaginatedResponse<Commission>['meta']): Pagination => {
  return {
    total: meta.total,
    perPage: meta.perPage,
    currentPage: meta.currentPage,
    lastPage: meta.lastPage,
    from: (meta.currentPage - 1) * meta.perPage + 1,
    to: Math.min(meta.currentPage * meta.perPage, meta.total),
  };
};

export const CommissionHistoryTable: React.FC<CommissionHistoryTableProps> = ({
  commissions,
  pagination,
  loading = false,
  onPageChange,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedRecurringCommission, setSelectedRecurringCommission] = useState<Commission | null>(
    null
  );
  const formatServiceDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(i18n.language, {
      dateStyle: 'medium',
      timeZone: 'UTC',
    });

  return (
    <Box pb={8}>
      {loading ? (
        <TableSkeleton
          rows={8}
          columns={[
            { width: '160px', type: 'date' },
            { width: '260px' },
            { width: '180px' },
            { width: '140px' },
            { width: '160px' },
          ]}
          stickyColumns={1}
          firstColumnWidth="160px"
        />
      ) : (
        <TableScrollArea>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader width="160px" {...stickyColumnProps('160px')}>
                  {t('commissions.history.table.date')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="260px">
                  {t('commissions.history.table.affiliate')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="180px">
                  {t('commissions.history.table.source')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="140px" textAlign="right">
                  {t('commissions.history.table.status')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="160px" textAlign="right">
                  {t('commissions.history.table.amount')}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {commissions.length === 0 ? (
                <TableEmptyState
                  colSpan={5}
                  icon={<MoneySquare width={22} height={22} />}
                  title={t('commissions.history.table.noRecords')}
                />
              ) : (
                commissions.map((commission) => {
                  const source = getCommissionSourcePresentation(commission);
                  const isRecurring = source.kind === 'recurring';
                  const recurringDisplayAmount = isRecurring
                    ? getRecurringDisplayAmount(commission)
                    : undefined;
                  return (
                    <Table.Row key={commission.id} _hover={{ bg: 'gray.50' }}>
                      <Table.Cell width="160px" {...stickyColumnProps('160px')}>
                        <DateCell dateString={source.occurredAt} />
                      </Table.Cell>
                      <Table.Cell width="260px">
                        <AffiliateInfo
                          name={getUserName(commission)}
                          avatarUrl={commission.user.profilePictureUrl}
                          programName={commission.program.name}
                          size="xs"
                        />
                      </Table.Cell>
                      <Table.Cell width="180px">
                        {isRecurring ? (
                          <VStack align="start" gap={0} minW={0}>
                            <Box fontSize="xs" fontWeight="medium">
                              {t('commissions.history.table.recurring')}
                            </Box>
                            <Box fontSize="xs" color="gray.fg" lineClamp={1}>
                              {t('commissions.history.table.subscription', {
                                reference: source.reference,
                              })}
                            </Box>
                            {source.servicePeriod && (
                              <Box fontSize="xs" color="gray.fg">
                                {t('commissions.history.table.servicePeriod', {
                                  startsAt: formatServiceDate(source.servicePeriod.startsAt),
                                  endsAt: formatServiceDate(source.servicePeriod.endsAt),
                                })}
                              </Box>
                            )}
                            <Button
                              size="xs"
                              variant="plain"
                              px={0}
                              onClick={() => setSelectedRecurringCommission(commission)}
                            >
                              {t('commissions.decision.actions.viewDetails')}
                            </Button>
                          </VStack>
                        ) : source.kind === 'detached' ? (
                          <Box fontSize="xs" color="gray.fg">
                            {t('commissions.history.table.sourceUnavailable')}
                          </Box>
                        ) : (
                          <HStack gap={1.5} flexWrap="wrap">
                            <EcommercePlatformBadge
                              platform={'shopify' as EcommercePlatforms}
                              orderNumber={source.reference || 'N/A'}
                              colorPalette="gray"
                              size="sm"
                            />
                            <SubscriptionSaleBadge
                              isSubscriptionSale={commission.isSubscriptionSale}
                            />
                          </HStack>
                        )}
                      </Table.Cell>

                      <Table.Cell width="140px" textAlign="right">
                        <VStack align="flex-end" gap={1}>
                          <OrderRewardBadge snapshot={commission.orderRewardSnapshot} />
                          <CommissionRewardReviewNotice
                            review={commission.rewardReview ?? commission.order?.rewardReview}
                          />
                          <OrderStatusBadge status={commission.status} size="sm" fontSize="10px" />
                        </VStack>
                      </Table.Cell>
                      <Table.Cell width="160px" textAlign="right">
                        {isRecurring ? (
                          recurringDisplayAmount ? (
                            <Box fontSize="sm" fontWeight="medium">
                              {formatExactCommissionMoney(recurringDisplayAmount, i18n.language)}
                            </Box>
                          ) : (
                            <Box fontSize="xs" color="gray.fg">
                              {t('commissions.history.table.amountUnavailable')}
                            </Box>
                          )
                        ) : (
                          <VStack align="flex-end" gapY={0}>
                            <Money
                              fontSize="14px"
                              value={commission.amount.amount}
                              currency={commission.amount.currencyCode}
                            />
                            <Money
                              fontSize="xs"
                              color="gray.fg"
                              fontWeight="normal"
                              value={commission.amount.amount}
                              currency={commission.amount.currencyCode}
                            />
                          </VStack>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table.Root>
        </TableScrollArea>
      )}

      {/* Pagination - Pass the pagination object directly */}
      {pagination && onPageChange && pagination.lastPage > 1 && (
        <PaginationControls
          pagination={adaptPagination(pagination)}
          onPageChange={onPageChange}
          isLoading={loading}
        />
      )}
      <RecurringCommissionDetailsDrawer
        commission={selectedRecurringCommission}
        open={selectedRecurringCommission !== null}
        onClose={() => setSelectedRecurringCommission(null)}
      />
    </Box>
  );
};

// =====================
// Utility Functions
// =====================
const getUserName = (commission: Commission): string => {
  return (
    [commission.user.firstName, commission.user.lastName].filter(Boolean).join(' ') ||
    `User ID: ${commission.user.id}`
  );
};

const getRecurringDisplayAmount = (commission: Commission) => {
  if (commission.source?.kind !== 'recurring') return undefined;

  const hasFrozenPayout = commission.status === 'processing' || commission.status === 'paid';

  return hasFrozenPayout && commission.payoutAmountExact
    ? commission.payoutAmountExact
    : commission.source.commissionPosition?.net;
};
